import * as React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import IncidentStatus from '@components/incidentStatus/IncidentStatus'
import Icons from '@components/icon/Icons'
import Choice from '@components/choice/Choice'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as FiltreStore from '@store/filtre/filtre'

import theme from '@theme/Theme'
import useI18n from '@i18n/useI18n'
import { findFamilyParents, findLocalisationParents, displayLocationString } from '@screens/incident/utils'
import { findManifestation } from '@screens/manifestations/manifestationsUtils'

declare interface Option {
  label: string
  value: any
  disabled?: boolean
}

interface Props {
  incident: IncidentResponse
  cardHeight?: number
  setCardHeight?: (height: number) => void
  action?: IconName
  onAction?: () => void
  onAffect?: (value: any) => void
  disabled?: boolean
  data?: Option[]
  dataSelected?: Option
  displayPrice?: boolean
}

const IncidentCard = ({
  incident,
  cardHeight,
  setCardHeight,
  action,
  onAction,
  onAffect,
  disabled,
  data,
  dataSelected,
  displayPrice,
}: Props) => {
  const i18n = useI18n()

  const manifestationsStore = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = findManifestation(manifestationsStore, incident.manifestationId)

  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const providers = useReducer(SocieteStore.store, (s) => s.services)
  const users = useReducer(FiltreStore.store, (s) => s.users)

  const prestation = activities.find((f) => f.id === incident.famillePrestationId)
  const familly = prestation && findFamilyParents(activities, prestation)

  const space = places.find((p) => p.id === incident.localisationId)
  const locations = space && findLocalisationParents(places, space)

  const location = displayLocationString(incident, locations || [], manifestation)

  const provider = providers.find((p) => p.id === incident.societePrestataireId)

  const author = users.find((u) => u.id === incident.utilisateurDeclarantId)

  return (
    <MainContainer
      height={cardHeight}
      onLayout={setCardHeight ? (e) => setCardHeight(e.nativeEvent.layout.height) : undefined}>
      <Header>
        <TitleContainer>
          <Title>{i18n.t('screens.incidentList.card.head', { number: incident.numero })}</Title>
          {displayPrice && (
            <Title>
              {incident.tarif
                ? i18n.t('screens.incidentList.card.price', { price: incident.tarif })
                : i18n.t('screens.incidentList.card.quotePrice')}
            </Title>
          )}
        </TitleContainer>
        <IncidentStatus incidentStatus={incident.status} />
      </Header>
      <Body>
        <TypeContainer>
          <Type>
            {i18n.t(`screens.incidentList.card.type.${incident.isInfrastructure ? `infrastructure` : `prestation`}`)}
          </Type>
        </TypeContainer>

        <PictosContainer>
          {incident.isPrioritaire && <Icons name="problem" color={theme.colors.negate} size={20} />}
        </PictosContainer>

        {!!provider && (
          <Provider numberOfLines={1} ellipsizeMode="tail">
            {provider.nameSociete}
          </Provider>
        )}

        <Family numberOfLines={3} ellipsizeMode="tail">
          {familly && familly[0].libelle + ' - '}
          {prestation && prestation.libelle}
        </Family>

        <Location numberOfLines={3} ellipsizeMode="tail">
          {location || i18n.t('screens.incidentList.card.unknownLocation')}
        </Location>

        <CreationDate>
          {i18n.t('screens.incidentList.card.creationDate', {
            date: new Date(incident.dateDeCreation),
            author: author ? `${author.prenom} ${author.nom}` : i18n.t('common.unknownUser'),
          })}
        </CreationDate>

        {!!data && !!onAffect && !!dataSelected && (
          <ChoiceContainer>
            <Family>{i18n.t('screens.edlMap.incidentSupportedBy')}</Family>
            <Choice data={data} onItemSelected={(item) => onAffect(item.value)} value={dataSelected} />
          </ChoiceContainer>
        )}
      </Body>

      {disabled && <Overlay />}

      {!!action && !!onAction && (
        <ButtonContainer activeOpacity={0.8} onPress={onAction}>
          <Icons name={action} color={theme.colors.primary} size={24} />
        </ButtonContainer>
      )}
    </MainContainer>
  )
}

const MainContainer = styled(View)<{ height?: number }>`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 360px;`}
  min-height:${(props) => props.height || 320}px;
  ${(props) => props.theme.constants.shadow};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.incident};
`

const Header = styled(View)`
  padding: 16px 24px 28px;
  background-color: ${(props) => props.theme.colors.background};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`

const TitleContainer = styled(View)`
  margin-bottom: 8px;
  flex-direction: row;
  justify-content: space-between;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const ButtonContainer = styled(TouchableOpacity)`
  position: absolute;
  padding: 16px 24px;
  top: 0;
  right: 0;
`

const Body = styled(View)`
  padding: 28px 24px 16px;
  background-color: ${(props) => props.theme.colors.incident};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`

const TypeContainer = styled(View)`
  padding: 5px 8px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.backgroundTertiary};
  position: absolute;
  top: -13px;
  left: 24px;
`

const Type = styled(Text)<{ grey?: boolean }>`
  ${(props) => props.theme.fonts.Caption1};
  ${(props) => props.grey && `color: ${props.theme.colors.secondaryText};`}
`

const PictosContainer = styled(View)`
  flex-direction: row;
  position: absolute;
  right: 24px;
  top: -10px;
  width: 45px;
  justify-content: flex-end;
`

const Provider = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  margin-bottom: 8px;
`

const Family = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  font-size: 14px;
  margin-bottom: 8px;
`

const Location = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
`

const CreationDate = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const ChoiceContainer = styled(View)`
  padding-top: 16px;
`

const Overlay = styled(View)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${(props) => props.theme.colors.incident};
  opacity: 0.7;
`

export default IncidentCard
